import type { PrivateFile } from './document';
import type { EventType } from './events';
import type { BaseFolder } from './folders';

/**
 * Incident vocabulary.
 *
 * Nature and consequences are two orthogonal axes on purpose. A single event
 * can damage equipment *and* injure someone, so a single enum would force the
 * declarant to choose and lose information at the source. The qualification
 * done in « Suivre » reuses the very same two axes: it confirms or corrects
 * what was declared, it never translates one vocabulary into another.
 */

/**
 * Attachment rules, shared on purpose. A declaration is made in the field, on
 * a phone: photos are the point, and a PDF is the occasional constat or
 * medical note. The file picker and the multer guard read the same three
 * constants, so the dialog can never offer what the API refuses.
 */
export const INCIDENT_ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'application/pdf',
] as const;

export const INCIDENT_MAX_FILES = 5;
export const INCIDENT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

// needed for zod (z.enum needs a readonly string tuple)
export const INCIDENT_NATURE_VALUES = [
  'DANGEROUS_SITUATION',
  'NEAR_MISS',
  'DAMAGE',
] as const;

export const IncidentNature = {
  DANGEROUS_SITUATION: 'DANGEROUS_SITUATION',
  NEAR_MISS: 'NEAR_MISS',
  DAMAGE: 'DAMAGE',
} as const;

export type IncidentNature = (typeof INCIDENT_NATURE_VALUES)[number];

export const INCIDENT_CONSEQUENCE_VALUES = [
  'INJURY',
  'MATERIAL_DAMAGE',
  'ENVIRONMENTAL',
  'OTHER',
] as const;

export const IncidentConsequence = {
  INJURY: 'INJURY',
  MATERIAL_DAMAGE: 'MATERIAL_DAMAGE',
  ENVIRONMENTAL: 'ENVIRONMENTAL',
  OTHER: 'OTHER',
} as const;

export type IncidentConsequence = (typeof INCIDENT_CONSEQUENCE_VALUES)[number];

export const INCIDENT_WORK_RELATION_VALUES = [
  'WORK_ACCIDENT',
  'COMMUTING_ACCIDENT',
  'NOT_WORK_RELATED',
] as const;

export type IncidentWorkRelation =
  (typeof INCIDENT_WORK_RELATION_VALUES)[number];

export const INCIDENT_SEVERITY_VALUES = [
  'MINOR',
  'MODERATE',
  'SERIOUS',
  'CRITICAL',
] as const;

export const IncidentSeverity = {
  MINOR: 'MINOR',
  MODERATE: 'MODERATE',
  SERIOUS: 'SERIOUS',
  CRITICAL: 'CRITICAL',
} as const;

export type IncidentSeverity = (typeof INCIDENT_SEVERITY_VALUES)[number];

export const INCIDENT_STATUS_VALUES = [
  'DECLARED',
  'IN_REVIEW',
  'CLOSED',
  'NOT_RETAINED',
] as const;

export const IncidentStatus = {
  DECLARED: 'DECLARED',
  IN_REVIEW: 'IN_REVIEW',
  CLOSED: 'CLOSED',
  NOT_RETAINED: 'NOT_RETAINED',
} as const;

export type IncidentStatus = (typeof INCIDENT_STATUS_VALUES)[number];

export const INCIDENT_PERSON_ROLE_VALUES = [
  'VICTIM',
  'WITNESS',
  'INVOLVED',
  'FIRST_AIDER',
] as const;

export type IncidentPersonRole = (typeof INCIDENT_PERSON_ROLE_VALUES)[number];

/**
 * The two statuses a `POST /incident/:id/close` can land on. `CLOSED` requires
 * a qualified incident; `NOT_RETAINED` is reachable straight from `DECLARED`
 * because a duplicate does not need qualifying before being set aside.
 */
export const INCIDENT_TERMINAL_STATUSES = ['CLOSED', 'NOT_RETAINED'] as const;

export type IncidentTerminalStatus =
  (typeof INCIDENT_TERMINAL_STATUSES)[number];

/**
 * A closed incident accepts no further change. Both sides ask this question —
 * the service to refuse a write, the UI to stop offering one — so the list of
 * terminal statuses is read here rather than re-ORed at each call site.
 */
export const isIncidentTerminal = (status: IncidentStatus): boolean =>
  INCIDENT_TERMINAL_STATUSES.some((terminal) => terminal === status);

/**
 * Closing means "we handled it", which cannot be true of an incident nobody
 * has qualified. Setting one aside as `NOT_RETAINED` is a different statement
 * and needs no qualification.
 */
export const canCloseIncident = (
  incident: Pick<IncidentListItem, 'nature'>,
): boolean => incident.nature !== null;

export type IncidentPersonIdentity = {
  id: number;
  role: IncidentPersonRole;
  // Exactly one of the two is set — enforced by a CHECK constraint in the
  // database, not only by the service.
  user: {
    id: number;
    firstName: string;
    lastName: string;
    externalUserId: string | null;
  } | null;
  externalName: string | null;
  externalCompany: string | null;
  externalContact: string | null;
};

export type IncidentAction = {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  label: string;
  dueDate: Date | null;
  completedAt: Date | null;
  completionNote: string | null;
  sortOrder: number;
  incidentId: number;
};

export type IncidentAttachment = {
  /** `UserDocument.id`, the row that federates every private document. */
  id: number;
  createdAt: Date;
  /** Pieces sent with the initial declaration can never be detached. */
  isImmutable: boolean;
  /**
   * Only what the list renders. The rest of `PrivateFile` — the R2 storage
   * key above all — has no business travelling to a browser.
   */
  file: Pick<PrivateFile, 'name' | 'mimeType'> | null;
};

export type IncidentQrCode = {
  id: number;
  createdAt: Date;
  token: string;
  label: string;
  folder: Pick<BaseFolder, 'id' | 'name'> | null;
  /** Number of declarations already made through this poster. */
  declarationCount: number;
};

/** What an unauthenticated visitor scanning a poster is allowed to see. */
export type IncidentDeclarationContext = {
  organizationName: string;
  organizationLogo: string | null;
  folder: Pick<BaseFolder, 'id' | 'name'> | null;
};

export type IncidentListItem = {
  id: number;
  reference: string;
  occurredAt: Date;
  declaredAt: Date;
  declaredNature: IncidentNature;
  declaredConsequences: IncidentConsequence[];
  declaredSeverity: IncidentSeverity;
  status: IncidentStatus;
  nature: IncidentNature | null;
  consequences: IncidentConsequence[];
  actualSeverity: IncidentSeverity | null;
  potentialSeverity: IncidentSeverity | null;
  isAnonymous: boolean;
  locationLabel: string | null;
  folder: Pick<BaseFolder, 'id' | 'name'> | null;
  /** Actions past their due date and not completed — computed on read. */
  overdueActionCount: number;
};

export type Incident = IncidentListItem & {
  createdAt: Date;
  updatedAt: Date;
  description: string;
  declaredByUser: {
    id: number;
    firstName: string;
    lastName: string;
  } | null;
  declarantContact: string | null;
  declaredViaQrCode: { id: number; label: string } | null;
  workRelation: IncidentWorkRelation | null;
  hasWorkStoppage: boolean | null;
  closedAt: Date | null;
  closingComment: string | null;
  immediateActions: string | null;
  factsTimeline: string | null;
  testimonies: string | null;
  evidence: string | null;
  equipments: { id: number; name: string; serialNumber: string | null }[];
  people: IncidentPersonIdentity[];
  actions: IncidentAction[];
  documents: IncidentAttachment[];
  /**
   * The record's chronology, newest first. A declaration made anonymously or
   * through a poster writes no event at all, so the detail view opens the
   * timeline with `declaredAt` rather than expecting a row here for it.
   */
  eventLogs: IncidentEventLog[];
};

/** One logged change to an incident, as its chronology renders it. */
export type IncidentEventLog = {
  id: number;
  createdAt: Date;
  type: EventType;
  ownerUser: { id: number; firstName: string; lastName: string };
};

/**
 * An action is late when its target day is behind us and nobody has ticked it
 * off. Never stored: a row would go stale the moment the clock ticks, and
 * keeping it fresh would need a cron for no gain.
 *
 * A due date is a day, not an instant: the form writes it from a date-only
 * input, which lands on midnight UTC. Comparing that raw would flag an action
 * due today as late for the whole of today, at an hour that moves with the
 * reader's timezone — so the end of the due day is what the clock is held
 * against, in the same UTC the input wrote.
 */
export const isIncidentActionOverdue = (
  action: Pick<IncidentAction, 'dueDate' | 'completedAt'>,
  now: Date = new Date(),
): boolean => {
  if (action.completedAt !== null || action.dueDate === null) return false;
  const endOfDueDay = new Date(action.dueDate);
  endOfDueDay.setUTCHours(23, 59, 59, 999);
  return endOfDueDay.getTime() < now.getTime();
};
